import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@plotlounge/shared/api";
import type { CreateNudgeInput } from "@plotlounge/shared/validations";
import { getApiClient } from "../api-client";

// ─── Types ───────────────────────────────────────────────────────────────

export type RoomStatus = "UPCOMING" | "ACTIVE" | "COMPLETED" | "ARCHIVED";

export interface RoomBookSummary {
  id: string;
  title: string;
  author: string | null;
  coverUrl: string | null;
  segmentCount?: number;
}

export interface RoomMemberProgress {
  userId: string;
  user: { id: string; displayName: string | null; image: string | null };
  joinedAt: string;
  lastActivity: string | null;
  segmentsRead: number[];
  segmentsReadCount: number;
  isBehind: boolean;
  progressPercentage: number;
}

export interface RoomProgressResponse {
  roomId: string;
  roomName: string;
  totalSegments: number;
  unlockedSegments: number;
  isFacilitator: boolean;
  members: RoomMemberProgress[];
  behindCount: number;
}

export interface CreateRoomSegmentInput {
  number: number;
  title: string;
  startPercent?: number;
  endPercent?: number;
}

export interface CreateRoomInput {
  clubId: string;
  bookTitle: string;
  bookAuthor: string;
  bookDescription?: string;
  bookCoverUrl?: string;
  bookPageCount?: number;
  openLibraryId?: string;
  googleBooksId?: string;
  isbn?: string;
  segments: CreateRoomSegmentInput[];
}

export interface CreatedRoom {
  id: string;
  name: string;
  bookClubId: string;
  bookId: string;
  facilitatorId: string;
  startDate: string;
  endDate: string;
  status: RoomStatus;
  book: RoomBookSummary;
}

export interface NudgeResponse {
  id: string;
  receiverId: string;
  senderId: string;
  message: string | null;
  createdAt?: string;
}

export interface RateLimitedError {
  retryAfterSeconds: number;
  resetAt: string | null;
}

export interface BookWithSegments {
  id: string;
  title: string;
  author: string | null;
  coverUrl: string | null;
  pageCount?: number | null;
  description?: string | null;
  openLibraryId?: string | null;
  googleBooksId?: string | null;
  isbn?: string | null;
  segments: Array<{
    id: string;
    segmentNumber: number;
    title: string | null;
    startPage: number | null;
    endPage: number | null;
    startPercent?: number | null;
    endPercent?: number | null;
  }>;
}

// ─── Query keys ──────────────────────────────────────────────────────────

export const roomProgressKey = (roomId: string) =>
  ["rooms", roomId, "progress"] as const;
export const bookDetailKey = (bookId: string) => ["books", bookId] as const;

// ─── Queries ─────────────────────────────────────────────────────────────

// Mirrors `BookCandidate` from `@plotlounge/shared/books` — `/books/search`
// returns external-provider candidates, not persisted rows.
export interface BookSearchResult {
  title: string;
  author: string;
  description?: string | null;
  coverUrl?: string | null;
  pageCount?: number | null;
  publishYear?: number | null;
  openLibraryId?: string | null;
  googleBooksId?: string | null;
  isbn?: string | null;
}

export function useBookSearch(query: string) {
  const trimmed = query.trim();
  return useQuery<BookSearchResult[]>({
    queryKey: ["clubs", "rooms", "book-search", trimmed],
    enabled: trimmed.length >= 2,
    queryFn: () =>
      getApiClient().get<BookSearchResult[]>(
        `/books/search?q=${encodeURIComponent(trimmed)}&limit=8`,
      ),
    staleTime: 30 * 1000,
  });
}

export function useBookDetail(bookId: string | undefined) {
  return useQuery<BookWithSegments>({
    queryKey: bookId ? bookDetailKey(bookId) : ["books", "_none"],
    enabled: Boolean(bookId),
    queryFn: () => getApiClient().get<BookWithSegments>(`/books/${bookId}`),
    staleTime: 60 * 1000,
  });
}

export function useRoomProgress(roomId: string | undefined) {
  return useQuery<RoomProgressResponse>({
    queryKey: roomId ? roomProgressKey(roomId) : ["rooms", "_none", "progress"],
    enabled: Boolean(roomId),
    queryFn: () =>
      getApiClient().get<RoomProgressResponse>(`/rooms/${roomId}/progress`),
    staleTime: 10 * 1000,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────

export function useCreateRoom() {
  return useMutation<CreatedRoom, Error, CreateRoomInput>({
    mutationFn: ({ clubId, ...body }) =>
      getApiClient().post<CreatedRoom>(`/clubs/${clubId}/rooms`, body),
  });
}

/**
 * Send a nudge to a single member of a room. Surfaces 429 RATE_LIMITED with
 * `retryAfterSeconds` so callers can show the daily-cap indicator without
 * polling.
 */
export function useNudgeMember(roomId: string) {
  const qc = useQueryClient();
  return useMutation<NudgeResponse, ApiError, CreateNudgeInput>({
    mutationFn: (input) =>
      getApiClient().post<NudgeResponse>(`/rooms/${roomId}/nudge`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: roomProgressKey(roomId) });
    },
  });
}

export function isRateLimitedError(error: unknown): error is ApiError & {
  data: { code: "RATE_LIMITED"; retryAfterSeconds?: number; resetAt?: string };
} {
  return (
    error instanceof ApiError &&
    error.status === 429 &&
    typeof error.data === "object" &&
    error.data !== null &&
    (error.data as { code?: string }).code === "RATE_LIMITED"
  );
}

export function extractRateLimitInfo(error: unknown): RateLimitedError | null {
  if (!isRateLimitedError(error)) return null;
  const data = error.data;
  return {
    retryAfterSeconds: data.retryAfterSeconds ?? 0,
    resetAt: data.resetAt ?? null,
  };
}
